import type { TenantAnalyticsAdapter, TenantAnalyticsEnvelope } from '../types';
import {
  type MetaPixelPublicConfig,
  parseMetaPixelPublicConfig,
} from './config';

/**
 * Meta Pixel standard-event parameters. Deliberately PII-free: no
 * user_data / advanced-matching fields exist in this contract (platform
 * decision — customer identifiers are never shared with ad platforms).
 */
export interface MetaPixelEventParameters {
  readonly content_ids?: readonly string[];
  readonly content_category?: string;
  readonly content_name?: string;
  readonly num_items?: number;
  readonly value?: number;
  readonly currency?: string;
}

export type MetaPixelEventName =
  | 'PageView'
  | 'ViewContent'
  | 'InitiateCheckout'
  | 'Purchase';

/**
 * Mirrors `fbq('track', name, parameters, { eventID })`. The eventID is the
 * canonical eventId, so a future Conversions API mirror deduplicates against
 * these browser events.
 */
export type MetaPixelEventSink = (
  eventName: MetaPixelEventName,
  parameters: MetaPixelEventParameters,
  options: { readonly eventID: string },
) => void | Promise<void>;

export interface CreateMetaPixelAdapterOptions {
  readonly publicConfig: unknown;
  readonly send: MetaPixelEventSink;
  readonly id?: string;
  readonly enabled?: boolean;
}

export function createMetaPixelAdapter(
  options: CreateMetaPixelAdapterOptions,
): TenantAnalyticsAdapter {
  const publicConfig = requirePublicConfig(options.publicConfig);

  if (typeof options.send !== 'function') {
    throw new Error('Meta Pixel requires an event sink.');
  }

  const send = options.send;

  return Object.freeze({
    id: options.id ?? `meta:${publicConfig.pixelId}`,
    provider: 'meta',
    // A pure advertising pixel: nothing is delivered without an explicit
    // marketing grant, unlike the measurement-consent Google transports.
    requiredConsent: Object.freeze(['advertising'] as const),
    enabled: options.enabled !== false,
    deliver: async (envelope: TenantAnalyticsEnvelope) => {
      requireSafeBrowserDocumentLocation(envelope);
      const mapped = mapMetaPixelEvent(envelope);

      if (mapped) {
        await send(mapped.eventName, mapped.parameters, {
          eventID: envelope.event.eventId,
        });
      }
    },
  });
}

function mapMetaPixelEvent(envelope: TenantAnalyticsEnvelope): {
  readonly eventName: MetaPixelEventName;
  readonly parameters: MetaPixelEventParameters;
} | null {
  const event = envelope.event;

  switch (event.name) {
    case 'page_view':
      return { eventName: 'PageView', parameters: Object.freeze({}) };

    case 'view_content':
    case 'begin_checkout':
      return {
        eventName:
          event.name === 'view_content' ? 'ViewContent' : 'InitiateCheckout',
        parameters: Object.freeze({
          content_ids: Object.freeze([event.contentId]),
          content_category: event.contentType,
          ...(event.contentName ? { content_name: event.contentName } : {}),
          ...(event.name === 'begin_checkout' ? { num_items: 1 } : {}),
          ...(event.value !== undefined
            ? { value: event.value, currency: event.currency }
            : {}),
        }),
      };

    case 'purchase':
      return {
        eventName: 'Purchase',
        parameters: Object.freeze({
          content_ids: Object.freeze(event.items.map((item) => item.itemId)),
          ...(event.items[0]?.category
            ? { content_category: event.items[0].category }
            : {}),
          num_items: event.items.length,
          value: event.value,
          currency: event.currency,
        }),
      };
  }
}

function requireSafeBrowserDocumentLocation(envelope: TenantAnalyticsEnvelope) {
  if (!envelope.event.browserDocumentLocationSafe) {
    throw new Error(
      'Meta Pixel delivery suppressed for an unsafe browser document location.',
    );
  }
}

function requirePublicConfig(value: unknown): MetaPixelPublicConfig {
  const publicConfig = parseMetaPixelPublicConfig(value);

  if (!publicConfig) {
    throw new Error('Invalid Meta Pixel public configuration.');
  }

  return publicConfig;
}
